/**
 * Utility functions for handling HTTP errors and generating user-friendly error messages
 */

interface HttpError {
  status?: number;
  message?: string;
  code?: string;
}

/**
 * Generates a user-friendly error message based on HTTP status code and error details
 * @param error - Error object with status, message, and other properties
 * @returns User-friendly error message string
 */
export const getErrorMessage = (error: unknown): string => {
  const defaultMessage = 'Failed to activate role. Please try again.';

  // Type guard for error objects
  if (!error || typeof error !== 'object') {
    return defaultMessage;
  }

  const httpError = error as HttpError;

  // Check if it's an HTTP error with status code
  if (typeof httpError.status === 'number') {
    switch (httpError.status) {
      case 400:
        return 'Invalid request. Please check your input and try again.';
      case 401:
        return 'Authentication required. Please log in and try again.';
      case 403:
        return 'You do not have permission to activate this role.';
      case 404:
        return 'Role not found. Please refresh the page and try again.';
      case 409:
        return 'Role conflict. This role may have already been activated.';
      case 422:
        return 'Invalid data provided. Please check your input and try again.';
      case 500:
        return 'Server error. Please try again later.';
      case 502:
      case 503:
      case 504:
        return 'Service temporarily unavailable. Please try again later.';
      default:
        return typeof httpError.message === 'string'
          ? httpError.message
          : defaultMessage;
    }
  }

  // Check for network errors
  if (
    typeof httpError.message === 'string' &&
    (httpError.message.includes('fetch') ||
      httpError.message.includes('network'))
  ) {
    return 'Network error. Please check your connection and try again.';
  }

  // Check for timeout errors
  if (
    typeof httpError.message === 'string' &&
    httpError.message.includes('timeout')
  ) {
    return 'Request timed out. Please try again.';
  }

  // Return the error message if available, otherwise default
  return typeof httpError.message === 'string'
    ? httpError.message
    : defaultMessage;
};

/**
 * Determines if an error is a network-related error
 * @param error - Error object to check
 * @returns True if it's a network error
 */
export const isNetworkError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const httpError = error as HttpError;

  return (
    (typeof httpError.message === 'string' &&
      (httpError.message.includes('fetch') ||
        httpError.message.includes('network') ||
        httpError.message.includes('Failed to fetch'))) ||
    httpError.code === 'NETWORK_ERROR'
  );
};

/**
 * Determines if an error is a server error (5xx status codes)
 * @param error - Error object to check
 * @returns True if it's a server error
 */
export const isServerError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const httpError = error as HttpError;

  return (
    typeof httpError.status === 'number' &&
    httpError.status >= 500 &&
    httpError.status < 600
  );
};

/**
 * Determines if an error is a client error (4xx status codes)
 * @param error - Error object to check
 * @returns True if it's a client error
 */
export const isClientError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const httpError = error as HttpError;

  return (
    typeof httpError.status === 'number' &&
    httpError.status >= 400 &&
    httpError.status < 500
  );
};

/**
 * Determines if an error indicates a role with the same name already exists
 * @param error - Error object to check
 * @returns True if it's a duplicate role name error
 */
export const isRoleAlreadyExistsError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const httpError = error as HttpError;
  const errorMessage =
    typeof httpError.message === 'string'
      ? httpError.message.toLowerCase()
      : '';

  return errorMessage.includes('already exist');
};
